
// src/components/AdminLayout/AdminLayout.jsx
import React, { useState, useEffect, useRef } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { BarChartFill, Box, List, Person, PlusCircle, Eye, EyeSlash, ChevronDown } from "react-bootstrap-icons";
import "./AdminLayout.css";
const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const AdminLayout = () => {
  const [showModal, setShowModal] = useState(false);
  const [adminData, setAdminData] = useState({
    name: "",
    email: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [showAdminDropdown, setShowAdminDropdown] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleChange = (e) => {
    setAdminData({ ...adminData, [e.target.name]: e.target.value });
    setError("");
  };

  useEffect(() => {
    const admin = localStorage.getItem("admin");
    if (admin) {
      const parsed = JSON.parse(admin);
      setCurrentAdmin(parsed);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAdminDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close sidebar when clicking on links (mobile)
  const handleNavClick = () => {
    if (window.innerWidth < 992) {
      setSidebarOpen(false);
    }
  };

  // Close sidebar on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(`${API}/api/admin/signup`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(adminData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to create admin");
      }

      setSuccess("Admin created successfully!");
      setTimeout(() => {
        setShowModal(false);
        setSuccess("");
        setAdminData({ name: "", email: "", password: "" });
      }, 1500);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("admin");
    window.location.href = "/";
  };

  return (
    <div className="admin-layout-container">
      {/* Mobile/Tablet Overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Fixed Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? "sidebar-open" : ""}`}>
        <div className="sidebar-inner">
          <h2 className="sidebar-logo">
            <span className="logo-accent">Medi</span>Store Admin
          </h2>

          <nav className="sidebar-nav">
            <ul className="sidebar-menu">
              <li>
                <NavLink
                  to="/"
                  end
                  className="sidebar-link"
                  onClick={handleNavClick}
                >
                  <BarChartFill size={20} />
                  <span>Dashboard</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/admin/products"
                  className="sidebar-link"
                  onClick={handleNavClick}
                >
                  <Box size={20} />
                  <span>Products</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/admin/orders"
                  className="sidebar-link"
                  onClick={handleNavClick}
                >
                  <List size={20} />
                  <span>Orders</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/admin/users"
                  className="sidebar-link"
                  onClick={handleNavClick}
                >
                  <Person size={20} />
                  <span>Users</span>
                </NavLink>
              </li>
              <li>
                <NavLink
                  to="/admin/queries"
                  className="sidebar-link"
                  onClick={handleNavClick}
                >
                  <Person size={20} />
                  <span>Queries</span>
                </NavLink>
              </li>
            </ul>
          </nav>
          <button className="logout-btn" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        <header className="admin-header">
          <div className="admin-header-inner">
            {/* Hamburger Menu - Mobile/Tablet */}
            <button
              className="hamburger-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle sidebar"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>

            <button
              className="admin-add-btn"
              onClick={() => setShowModal(true)}
            >
              <PlusCircle size={18} /> Add Admin
            </button>
            {/* Admin Profile Dropdown - Dynamic */}
            {currentAdmin && (
              <div className="profile-dropdown" ref={dropdownRef}>
                <button
                  className="profile-btn"
                  onClick={() => setShowAdminDropdown(!showAdminDropdown)}
                >
                  {/* Name & Email - Desktop only */}
                  <div className="profile-info d-none d-lg-block">
                    <div className="profile-name">{currentAdmin.name}</div>
                    <div className="profile-email">{currentAdmin.email}</div>
                  </div>

                  {/* Avatar with Initials */}
                  <div className="profile-avatar">
                    {getInitials(currentAdmin.name)}
                    <ChevronDown className="chevron-icon" size={16} />
                  </div>
                </button>

                {/* Dropdown Menu */}
                {showAdminDropdown && (
                  <div className="profile-menu">
                    <div className="profile-header">
                      <div className="profile-name">{currentAdmin.name}</div>
                      <div className="profile-email">{currentAdmin.email}</div>
                    </div>
                    {/* <hr className="profile-divider" /> */}
                  </div>
                )}
              </div>
            )}
          </div>
        </header>

        <div className="page-content">
          <Outlet />
        </div>
      </main>

      {showModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3 className="modal-title">Add New Admin</h3>

            <form onSubmit={handleSubmit}>
              <div className="modal-input-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Full Name"
                  value={adminData.name}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="modal-input-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={adminData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="modal-input-group password-input-group">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={adminData.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={togglePasswordVisibility}
                  tabIndex="-1"
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>

              {/* Feedback Messages */}
              {error && <p className="modal-error">{error}</p>}
              {success && <p className="modal-success">{success}</p>}

              <div className="modal-actions">
                <button
                  type="button"
                  className="modal-cancel-btn"
                  onClick={() => {
                    setShowModal(false);
                    setError("");
                    setSuccess("");
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="modal-save-btn"
                  disabled={loading}
                >
                  {loading ? "Creating..." : "Save Admin"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLayout;
